import * as React from 'react';
import {
  Checkbox,
  CheckboxChangeEvent,
} from '@revfluence/fresh';
import { DollarCircleOutlined } from '@ant-design/icons';
import { CalendarCheckIcon, CircleInfoIcon } from '@revfluence/fresh-icons/regular/esm';
import { isBefore } from 'date-fns';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { Button } from '@frontend/shadcn/components/ui/button';
import { Calendar } from '@frontend/shadcn/components/ui/calendar';
import { cn } from '@frontend/shadcn/lib/utils';
import { format } from 'date-fns';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import ShadCnAlert from '@frontend/app/refresh-components/Alert';
import {
  TooltipHeader,
  TooltipTitle,
  TooltipBody,
  TooltipItem,
  TooltipItemValue,
  TooltipArrow,
} from '@frontend/applications/Social/components/tooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { AUTOMATED_PAYOUT_ID, AutomatedPayoutLabel } from '@frontend/applications/AffiliatesApp/constants/offers';
import { isNull } from 'lodash';
import { TSelectedMember } from '../../types';
import { IPayoutVariant } from '../../../OfferForm';

const { useEffect, useMemo } = React;
const message = 'The default payout option and active dates will be applied to all the members. You can also customize the promo code name, payout and code active/end date for each of them by clicking on Customize Per Member button.';
interface IProps {
  members: readonly TSelectedMember[] | null;
  payouts: IPayoutVariant[] | null;
  defaultPayoutId: number | null;
  bulkPayoutId: number;
  bulkStartDate: Date,
  bulkEndDate: Date;
  offerStartDate: string;
  showEndDate?: boolean;
  onBulkPayoutIdChange: (payoutId: number) => void;
  onBulkActiveDateChange: (field: string, value: Date) => void;
  updateShowEndDate: (showEndDate: boolean) => void;
  isAutomatedPayoutEnabled: boolean;
}

export const RefreshBulkUpdate: React.FC<Readonly<IProps>> = (props) => {
  const {
    members, payouts, defaultPayoutId, onBulkPayoutIdChange, onBulkActiveDateChange, bulkStartDate, bulkEndDate, offerStartDate, showEndDate, updateShowEndDate, isAutomatedPayoutEnabled,
  } = props;
  const { automatePayoutTier } = useClientFeatures();
  useEffect(() => {
    onBulkPayoutIdChange(defaultPayoutId);
  }, [defaultPayoutId, onBulkPayoutIdChange]);
  useEffect(() => {
    onBulkActiveDateChange('bulkStartDate', offerStartDate ? new Date(offerStartDate) : new Date());
  }, [onBulkActiveDateChange, offerStartDate]);
  const onEndDateChecboxChange = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      updateShowEndDate(true);
    } else {
      onBulkActiveDateChange('bulkEndDate', null);
      updateShowEndDate(false);
    }
  };
  const disableEndDate = useMemo(() => {
    const givenDate = bulkStartDate ? isBefore(bulkStartDate.getTime(), new Date()) ? new Date() : new Date(bulkStartDate.getTime() + 60000) : bulkStartDate;
    return (current) => current && current < givenDate;
  }, [bulkStartDate]);
  const disableStartDate = (current) => {
    if (!offerStartDate) {
      return true;
    }
    const startDate = new Date(offerStartDate);
    return current && current < startDate;
  };
  const endDateErrorMessage = useMemo(() => {
    if (showEndDate) {
      if (!bulkEndDate) return 'Add End Date';
      if (showEndDate && isBefore(bulkEndDate, new Date())) {
        return 'End Date should be greater than todays date';
      }
      if (isBefore(bulkEndDate, bulkStartDate)) {
        return 'End Date should be greater than Active Date';
      }
    }
    return '';
  }, [showEndDate, bulkEndDate, bulkStartDate]);

  const payoutOptions: { id: number | null, label: string }[] = useMemo(() => {
    if (automatePayoutTier && isAutomatedPayoutEnabled) {
      const automatedPayouts = payouts.filter((payout) => typeof payout.automationThreshold === 'number' && payout.automationThreshold >= 0);
      const regularPayouts = payouts.filter((payout) => payout.automationThreshold === undefined || payout.automationThreshold === null);

      const options = automatedPayouts.length > 0
        ? [{ id: AUTOMATED_PAYOUT_ID, label: AutomatedPayoutLabel }]
        : [];

      regularPayouts.forEach((payout) => {
        const rules = (payout as any).offerPayoutRules || payout.payoutRules || [];
        let option = null;
        if (rules.length > 0) {
          option = { id: payout.id, label: `${payout.label} (Multiple)` };
        } else if (!isNull(payout.percentPayout) && !isNull(payout.flatPayout)) {
          option = { id: payout.id, label: `${payout.label} (${payout.percentPayout}% + $${payout.flatPayout})` };
        } else if (!isNull(payout.flatPayout)) {
          option = { id: payout.id, label: `${payout.label} ($${payout.flatPayout})` };
        } else if (!isNull(payout.percentPayout)) {
          option = { id: payout.id, label: `${payout.label} (${payout.percentPayout}%)` };
        }
        if (option) options.push(option);
      });

      return options;
    } else {
      return payouts.map((payout) => {
        const rules = (payout as any).offerPayoutRules || payout.payoutRules || [];
        if (rules.length > 0) {
          return { id: payout.id, label: `${payout.label} (Multiple)` };
        } else if (!isNull(payout.percentPayout) && !isNull(payout.flatPayout)) {
          return { id: payout.id, label: `${payout.label} (${payout.percentPayout}% + $${payout.flatPayout})` };
        } else if (!isNull(payout.flatPayout)) {
          return { id: payout.id, label: `${payout.label} ($${payout.flatPayout})` };
        } else if (!isNull(payout.percentPayout)) {
          return { id: payout.id, label: `${payout.label} (${payout.percentPayout}%)` };
        }
        return null;
      }).filter(Boolean);
    }
  }, [payouts, automatePayoutTier, isAutomatedPayoutEnabled]);
  return (
    <div className="flex flex-col gap-4 p-4">
      <ShadCnAlert
        title="Set Payout and Active Dates"
        description={message}
        type="info"
        closeable
      />
      <p className="m-0 text-sm font-medium text-primary">
        {members.length}
        {' '}
        {members.length === 1 ? 'Member' : 'Members'}
        {' '}
        Selected
      </p>
      <div className="flex flex-col gap-4">
        <Card className="p-4">
          <CardContent className="p-0 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="m-0 text-sm font-medium text-primary">
                <DollarCircleOutlined className="mr-2 text-teal-4" />
                Set Payout Value
              </p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleInfoIcon className="cursor-pointer text-primary" fontSize={14} />
                </TooltipTrigger>
                <TooltipContent
                  className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
                  side="bottom"
                  align="start"
                  sideOffset={5}
                  role="tooltip"
                  aria-live="polite"
                >
                  <TooltipHeader>
                    <TooltipTitle>Set Payout in Bulk</TooltipTitle>
                  </TooltipHeader>
                  <TooltipBody>
                    <TooltipItem>
                      <TooltipItemValue>
                        Choose the payout value for all the selected member
                      </TooltipItemValue>
                    </TooltipItem>
                  </TooltipBody>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </div>

            <Select
              defaultValue={automatePayoutTier && isAutomatedPayoutEnabled ? defaultPayoutId?.toString() || AUTOMATED_PAYOUT_ID.toString() : defaultPayoutId?.toString()}
              onValueChange={(value) => onBulkPayoutIdChange(Number(value))}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select payout value" />
              </SelectTrigger>
              <SelectContent>
                {payoutOptions.map((payout) => (
                  <SelectItem key={payout.id} value={payout.id.toString()}>
                    {payout.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
        <Card className="p-4">
          <CardContent className="p-0 flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <p className="m-0 text-sm font-medium text-primary">
                <CalendarCheckIcon style={{ marginRight: '8px' }} />
                Set Active Dates
              </p>
              <Tooltip>
                <TooltipTrigger asChild>
                  <CircleInfoIcon className="cursor-pointer text-primary" fontSize={14} />
                </TooltipTrigger>
                <TooltipContent
                  className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
                  side="bottom"
                  align="start"
                  sideOffset={5}
                  role="tooltip"
                  aria-live="polite"
                >
                  <TooltipHeader>
                    <TooltipTitle>Set Dates in Bulk</TooltipTitle>
                  </TooltipHeader>
                  <TooltipBody>
                    <TooltipItem>
                      <TooltipItemValue>
                        Now you can directly set active/end dates for all the selected members in bulk.
                      </TooltipItemValue>
                    </TooltipItem>
                  </TooltipBody>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            </div>
            <p className="m-0 text-sm font-medium text-primary">Active Date</p>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !bulkStartDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarCheckIcon className="mr-2 h-4 w-4" />
                  {bulkStartDate ? format(bulkStartDate, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 z-[9999] pointer-events-auto select-text" align="start">
                <Calendar
                  mode="single"
                  selected={bulkStartDate}
                  onSelect={(date) => onBulkActiveDateChange('bulkStartDate', date)}
                  disabled={(date) => disableStartDate(date)}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {!bulkStartDate && (
              <p className="m-0 text-sm font-medium text-red-4">Add Start Date</p>
            )}
            <Checkbox onChange={onEndDateChecboxChange} checked={showEndDate}>Set End Date</Checkbox>
            {showEndDate && (
              <>
                <p className="m-0 text-sm font-medium text-primary">End Date</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        'w-full justify-start text-left font-normal',
                        !bulkEndDate && 'text-muted-foreground'
                      )}
                    >
                      <CalendarCheckIcon className="mr-2 h-4 w-4" />
                      {bulkEndDate ? format(bulkEndDate, 'PPP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 z-[9999] pointer-events-auto select-text" align="start">
                    <Calendar
                      mode="single"
                      selected={bulkEndDate}
                      onSelect={(date) => onBulkActiveDateChange('bulkEndDate', date)}
                      disabled={(date) => disableEndDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                {!!endDateErrorMessage && (
                  <p className="m-0 text-sm font-medium text-red-4">{endDateErrorMessage}</p>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
